import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { generateSpawnMarkerLevel } from './spawnMarkerLevelGenerator.js';
import { getLightingQuality } from '../engine/lightingQuality.js';
import { createGridGlowOverlay, updateGridGlowOverlay } from './fx/gridGlowOverlay.js';

const GRID_W = 40;
const GRID_H = 40;
const CELL_SIZE = 3;
const HALF_W = (GRID_W * CELL_SIZE) / 2;
const HALF_H = (GRID_H * CELL_SIZE) / 2;

const FLOOR_COLOR = 0x1a1a1e;
const FLOOR_COLOR_ALT = 0x17171b;
const WALL_COLOR = 0x141418;
const WALL_TOP_COLOR = 0x222228;
const WALL_EMISSIVE_COLOR = 0x111118;
const DOOR_COLOR_LOCKED = 0xff4060;
const DOOR_COLOR_OPEN = 0x8afff0;

const WALL_H = 2.8;
const TILE_H_VARIANCE = 0.07;
const WALL_THICKNESS_SCALE_DEFAULT = 1;
const DOOR_BLOCKER_HALF_WIDTH = 0.52 * CELL_SIZE;
const DOOR_BLOCKER_HALF_DEPTH = 0.3 * CELL_SIZE;
const MIN_DOOR_SEPARATION = GRID_H * CELL_SIZE * 0.7;
const MIN_ENEMY_PLAYER_DIST = 12;
const BOSS_ARENA_SIZE = 84;
const TORCH_COLORS = [0xe8820a, 0xd46a06, 0xf5a020, 0xc05505];
const SKULL_ACCENT_COLOR = 0x4aa8ff;
const FLOOR_MARK_COLOR = 0x1f2c40;
const WALL_RIM_COLOR = 0x4fc3f7;
const WALL_RIM_EMISSIVE = 0x4fc3f7;
const WALL_RIM_EMISSIVE_INTENSITY = 0.7;
const WALL_RIM_HEIGHT = 0.1;
const WALL_AO_WIDTH = 0.3;
const LIGHTING_QUALITY = getLightingQuality();
const CHAIR_OBJ_PATHS = Object.freeze([
  '/assets/models/Chairs/Office_Chair_Black_01.obj',
  '/assets/models/Chairs/Office_Chair_Black_02.obj',
  '/assets/models/Chairs/Office_Chair_Brown_01.obj',
]);
const _objLoader = new OBJLoader();
const _chairTemplateCache = new Map();
const _chairTemplatePromise = new Map();

function _loadChairTemplate(path) {
  if (_chairTemplateCache.has(path)) return Promise.resolve(_chairTemplateCache.get(path));
  if (_chairTemplatePromise.has(path)) return _chairTemplatePromise.get(path);
  const p = new Promise((resolve) => {
    _objLoader.load(
      path,
      (obj) => {
        _chairTemplateCache.set(path, obj);
        resolve(obj);
      },
      undefined,
      () => resolve(null),
    );
  });
  _chairTemplatePromise.set(path, p);
  return p;
}

function cellToWorld(cx, cy) {
  return {
    x: (cx + 0.5) * CELL_SIZE - HALF_W,
    z: (cy + 0.5) * CELL_SIZE - HALF_H,
  };
}

function worldToCell(x, z) {
  const cx = Math.floor((x + HALF_W) / CELL_SIZE);
  const cy = Math.floor((z + HALF_H) / CELL_SIZE);
  return { cx, cy };
}

function inBounds(cx, cy) {
  return cx >= 0 && cx < GRID_W && cy >= 0 && cy < GRID_H;
}

function keyOf(cx, cy) {
  return `${cx},${cy}`;
}

export class ProceduralRoom {
  constructor({ scene, seed, wallThicknessScale = WALL_THICKNESS_SCALE_DEFAULT, layoutType = 'maze', arenaSize = BOSS_ARENA_SIZE }) {
    this.scene = scene;
    this.seed = seed;
    this.wallThicknessScale = Math.max(0.1, Number.isFinite(wallThicknessScale) ? wallThicknessScale : WALL_THICKNESS_SCALE_DEFAULT);
    this.wallThickness = CELL_SIZE * this.wallThicknessScale;
    this.wallHalfThickness = this.wallThickness / 2;
    this.doorBlockerHalfWidth = DOOR_BLOCKER_HALF_WIDTH * this.wallThicknessScale;
    this.doorBlockerHalfDepth = DOOR_BLOCKER_HALF_DEPTH * this.wallThicknessScale;
    this.group = new THREE.Group();
    this.walls = [];
    this.enemySpawns = [];
    this.playerSpawn = { x: 0, z: HALF_H - 2 };
    this.bossSpawn = { x: 0, z: 0 };
    this.doorA = null;
    this.doorB = null;
    this.door = null;
    this.doorOpen = false;
    this._diagToggle = false;
    this._walkable = new Set();
    this._walkableCells = [];
    this._floorAabb = { minX: -HALF_W, maxX: HALF_W, minZ: -HALF_H, maxZ: HALF_H };
    this._wallBoxes = [];
    this._propColliders = [];
    this._levelStats = null;
    this.layoutType = layoutType;
    this.arenaSize = Math.max(24, Number.isFinite(arenaSize) ? arenaSize : BOSS_ARENA_SIZE);
    this._boundsHalfW = HALF_W;
    this._boundsHalfH = HALF_H;
    this._navRevision = 0;
    this._isDisposed = false;

    this._build();
    this.scene.add(this.group);
  }

  getDebugInfo() {
    return {
      seed: this.seed >>> 0,
      doorA: this.doorA ? { ...this.doorA } : null,
      doorB: this.doorB ? { ...this.doorB } : null,
      levelStats: this._levelStats ? { ...this._levelStats } : null,
      playerSpawn: { ...this.playerSpawn },
      bossSpawn: { ...this.bossSpawn },
      enemySpawns: this.enemySpawns.map((p) => ({ ...p })),
    };
  }

  _build() {
    if (this.layoutType === 'bossArena') {
      this._buildBossArena();
      return;
    }
    const generated = generateSpawnMarkerLevel(this.seed, {
      gridW: GRID_W,
      gridH: GRID_H,
      corridorWidth: 3,
      depth: 3,
    });
    const { grid, rooms, spawns, maxD } = generated;

    const floorMat = new THREE.MeshStandardMaterial({ color: FLOOR_COLOR, roughness: 1, metalness: 0 });
    const wallMat = new THREE.MeshStandardMaterial({
      color: WALL_COLOR,
      emissive: WALL_EMISSIVE_COLOR,
      emissiveIntensity: 0.02,
      roughness: 0.9,
      metalness: 0,
    });
    const wallTopMat = new THREE.MeshStandardMaterial({
      color: WALL_TOP_COLOR,
      emissive: WALL_EMISSIVE_COLOR,
      emissiveIntensity: 0.03,
      roughness: 0.84,
      metalness: 0.05,
    });

    const floorGeo = new THREE.BoxGeometry(CELL_SIZE, 0.14, CELL_SIZE);
    const wallGeo = new THREE.BoxGeometry(this.wallThickness, WALL_H, this.wallThickness);
    const wallTopGeo = new THREE.BoxGeometry(this.wallThickness, 0.14, this.wallThickness);
    const wallRimGeo = new THREE.BoxGeometry(this.wallThickness, WALL_RIM_HEIGHT, this.wallThickness);
    const wallRimMat = new THREE.MeshStandardMaterial({
      color: WALL_RIM_COLOR,
      emissive: WALL_RIM_EMISSIVE,
      emissiveIntensity: WALL_RIM_EMISSIVE_INTENSITY,
      roughness: 0.6,
      metalness: 0.05,
    });
    const wallAoMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });

    for (let cy = 0; cy < GRID_H; cy++) {
      for (let cx = 0; cx < GRID_W; cx++) {
        const v = grid[cy * GRID_W + cx];
        if (v === 0) continue;
        const p = cellToWorld(cx, cy);
        if (v === 1) {
          const floor = new THREE.Mesh(floorGeo, floorMat);
          floor.position.set(p.x, -0.07, p.z);
          floor.receiveShadow = true;
          this.group.add(floor);
          this._walkable.add(keyOf(cx, cy));
          this._walkableCells.push({ cx, cy, x: p.x, z: p.z });
        } else if (v === 2) {
          const wall = new THREE.Mesh(wallGeo, wallMat);
          wall.position.set(p.x, WALL_H / 2, p.z);
          wall.castShadow = true;
          wall.receiveShadow = true;
          this.group.add(wall);
          const cap = new THREE.Mesh(wallTopGeo, wallTopMat);
          cap.position.set(p.x, WALL_H + 0.07, p.z);
          this.group.add(cap);
          const rim = new THREE.Mesh(wallRimGeo, wallRimMat);
          rim.position.set(p.x, WALL_H + 0.14, p.z);
          this.group.add(rim);
          const ao = new THREE.Mesh(
            new THREE.PlaneGeometry(this.wallThickness + WALL_AO_WIDTH, this.wallThickness + WALL_AO_WIDTH),
            wallAoMat,
          );
          ao.rotation.x = -Math.PI / 2;
          ao.position.set(p.x, 0.015, p.z);
          this.group.add(ao);
          const wallBox = {
            minX: p.x - this.wallHalfThickness,
            maxX: p.x + this.wallHalfThickness,
            minZ: p.z - this.wallHalfThickness,
            maxZ: p.z + this.wallHalfThickness,
          };
          this.walls.push(wallBox);
          this._wallBoxes.push(wallBox);
        }
      }
    }

    const toWorld = (spawn) => {
      const p = cellToWorld(spawn.cellX, spawn.cellY);
      return { x: p.x, z: p.z };
    };
    const spawnByType = (type) => spawns.filter((s) => s.type === type);

    const doorASpawn = spawnByType('doorA')[0];
    const doorBSpawn = spawnByType('doorB')[0];
    const bossSpawn = spawnByType('boss')[0];
    const enemySpawns = spawnByType('enemy');

    if (doorASpawn) this.doorA = toWorld(doorASpawn);
    if (doorBSpawn) this.doorB = toWorld(doorBSpawn);
    this.playerSpawn = this.doorA || this._fallbackDoorAnchor('bottom');
    this.bossSpawn = bossSpawn ? toWorld(bossSpawn) : this._pickBestSpawnForZone('center', { clearRadius: 3.2, minDistFromPlayer: 10 }) || this.playerSpawn;
    this.enemySpawns = enemySpawns.map(toWorld);
    if (this.enemySpawns.length === 0) this.enemySpawns = this._buildEnemyAnchors();
    if (!this.doorA || !this.doorB) this._resolveDoorAnchors();
    this._levelStats = {
      roomCount: rooms.length,
      enemyCount: this.enemySpawns.length,
      doorDistanceTiles: maxD,
    };
    this._addFloorPattern(this._boundsHalfW, this._boundsHalfH, { dense: true });
    this._addPerimeterProps(this._boundsHalfW, this._boundsHalfH, { dense: true });
    this._spawnDistributedChairProps({ count: 14, minDoorDistance: 6.2 });
    this._addPerimeterAccentLights(this._boundsHalfW, this._boundsHalfH, { dense: true });
    this._buildExitDoor();
  }

  _buildBossArena() {
    const half = this.arenaSize / 2;
    this._boundsHalfW = half;
    this._boundsHalfH = half;
    this._floorAabb = { minX: -half, maxX: half, minZ: -half, maxZ: half };

    const floorMat = new THREE.MeshStandardMaterial({ color: FLOOR_COLOR, roughness: 1, metalness: 0 });
    const wallMat = new THREE.MeshStandardMaterial({
      color: WALL_COLOR,
      emissive: WALL_EMISSIVE_COLOR,
      emissiveIntensity: 0.02,
      roughness: 0.9,
      metalness: 0,
    });
    const wallTopMat = new THREE.MeshStandardMaterial({
      color: WALL_TOP_COLOR,
      emissive: WALL_EMISSIVE_COLOR,
      emissiveIntensity: 0.03,
      roughness: 0.84,
      metalness: 0.05,
    });

    const floorGeo = new THREE.PlaneGeometry(this.arenaSize, this.arenaSize);
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    this.group.add(floor);

    const wallT = this.wallThickness;
    const wallEWGeo = new THREE.BoxGeometry(this.arenaSize + wallT, WALL_H, wallT);
    const wallNSGeo = new THREE.BoxGeometry(wallT, WALL_H, this.arenaSize + wallT);
    const topCapEWGeo = new THREE.BoxGeometry(this.arenaSize + wallT, 0.14, wallT);
    const topCapNSGeo = new THREE.BoxGeometry(wallT, 0.14, this.arenaSize + wallT);

    const wallRimMat = new THREE.MeshStandardMaterial({
      color: WALL_RIM_COLOR,
      emissive: WALL_RIM_EMISSIVE,
      emissiveIntensity: WALL_RIM_EMISSIVE_INTENSITY,
      roughness: 0.6,
      metalness: 0.05,
    });
    const wallAoMat = new THREE.MeshBasicMaterial({
      color: 0x000000,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
    });
    const addWall = (geo, capGeo, x, z, spanW, spanD) => {
      const wall = new THREE.Mesh(geo, wallMat);
      wall.position.set(x, WALL_H / 2, z);
      wall.castShadow = true;
      wall.receiveShadow = true;
      this.group.add(wall);
      const cap = new THREE.Mesh(capGeo, wallTopMat);
      cap.position.set(x, WALL_H + 0.07, z);
      this.group.add(cap);
      const rim = new THREE.Mesh(
        new THREE.BoxGeometry(spanW, WALL_RIM_HEIGHT, spanD),
        wallRimMat,
      );
      rim.position.set(x, WALL_H + 0.14, z);
      this.group.add(rim);
      const ao = new THREE.Mesh(
        new THREE.PlaneGeometry(spanW + WALL_AO_WIDTH, spanD + WALL_AO_WIDTH),
        wallAoMat,
      );
      ao.rotation.x = -Math.PI / 2;
      ao.position.set(x, 0.015, z);
      this.group.add(ao);
    };

    addWall(wallEWGeo, topCapEWGeo, 0, -half - wallT / 2, this.arenaSize + wallT, wallT);
    addWall(wallEWGeo, topCapEWGeo, 0, half + wallT / 2, this.arenaSize + wallT, wallT);
    addWall(wallNSGeo, topCapNSGeo, -half - wallT / 2, 0, wallT, this.arenaSize + wallT);
    addWall(wallNSGeo, topCapNSGeo, half + wallT / 2, 0, wallT, this.arenaSize + wallT);

    const pushWall = (minX, maxX, minZ, maxZ) => {
      const wall = { minX, maxX, minZ, maxZ };
      this.walls.push(wall);
      this._wallBoxes.push(wall);
    };
    pushWall(-half - wallT, half + wallT, -half - wallT, -half);
    pushWall(-half - wallT, half + wallT, half, half + wallT);
    pushWall(-half - wallT, -half, -half, half);
    pushWall(half, half + wallT, -half, half);

    const step = CELL_SIZE;
    for (let z = -half + step / 2; z <= half - step / 2; z += step) {
      for (let x = -half + step / 2; x <= half - step / 2; x += step) {
        this._walkableCells.push({ cx: 0, cy: 0, x, z });
      }
    }

    this.doorA = { x: 0, z: half - 2.5 };
    this.doorB = { x: 0, z: -half + 2.5 };
    this.playerSpawn = { ...this.doorA };
    this.bossSpawn = { x: 0, z: 0 };
    this.enemySpawns = [
      { x: -half * 0.45, z: -half * 0.25 },
      { x: half * 0.45, z: -half * 0.25 },
      { x: -half * 0.38, z: half * 0.2 },
      { x: half * 0.38, z: half * 0.2 },
    ];
    this._levelStats = {
      roomCount: 1,
      enemyCount: this.enemySpawns.length,
      doorDistanceTiles: Math.round((this.arenaSize - 5) / CELL_SIZE),
    };
    this._addFloorPattern(half, half, { dense: false });
    this._addPerimeterAccentLights(half, half, { dense: false });
    this._addPerimeterProps(half, half, { dense: false });
    this._spawnDistributedChairProps({ count: 8, minDoorDistance: 7.8 });
    this._buildExitDoor();
  }

  _spawnDistributedChairProps({ count = 12, minDoorDistance = 6 } = {}) {
    if (!Array.isArray(this._walkableCells) || this._walkableCells.length === 0) return;
    const placed = [];
    for (let i = 0; i < this._walkableCells.length && placed.length < count; i++) {
      const idx = ((this.seed + i * 29) >>> 0) % this._walkableCells.length;
      const cell = this._walkableCells[idx];
      if (!this._hasClearance(cell.x, cell.z, 1.2)) continue;
      if (this.doorA && Math.hypot(cell.x - this.doorA.x, cell.z - this.doorA.z) < minDoorDistance) continue;
      if (this.doorB && Math.hypot(cell.x - this.doorB.x, cell.z - this.doorB.z) < minDoorDistance) continue;
      if (Math.hypot(cell.x - this.playerSpawn.x, cell.z - this.playerSpawn.z) < minDoorDistance) continue;
      if (placed.some((p) => Math.hypot(cell.x - p.x, cell.z - p.z) < 4.2)) continue;
      placed.push({ x: cell.x, z: cell.z });
    }
    placed.forEach((p, i) => {
      this._spawnChairObjAt({
        x: p.x,
        z: p.z,
        rotY: (i % 8) * (Math.PI / 4),
        scale: 0.88 + ((i % 3) * 0.04),
        variant: i,
      });
    });
  }

  _spawnChairObjAt({ x, z, rotY = 0, scale = 0.92, variant = 0 }) {
    const path = CHAIR_OBJ_PATHS[Math.abs(variant) % CHAIR_OBJ_PATHS.length];
    _loadChairTemplate(path).then((src) => {
      if (!src || this._isDisposed) return;
      const chair = src.clone(true);
      chair.position.set(x, 0, z);
      chair.rotation.y = rotY;
      chair.scale.setScalar(scale);
      chair.traverse((node) => {
        if (!node.isMesh) return;
        node.castShadow = true;
        node.receiveShadow = true;
        node.userData.isProp = true;
        if (node.material) {
          const mats = Array.isArray(node.material) ? node.material : [node.material];
          for (const m of mats) m.side = THREE.DoubleSide;
        }
      });
      const baseBox = new THREE.Box3().setFromObject(chair);
      const size = new THREE.Vector3();
      baseBox.getSize(size);
      if (size.y > 0.001) {
        chair.scale.multiplyScalar(0.95 / size.y);
      }
      const floorBox = new THREE.Box3().setFromObject(chair);
      chair.position.y -= floorBox.min.y;
      this.group.add(chair);
      const colBox = new THREE.Box3().setFromObject(chair);
      const colSize = new THREE.Vector3();
      const colCenter = new THREE.Vector3();
      colBox.getSize(colSize);
      colBox.getCenter(colCenter);
      const shrink = 0.7;
      const halfW = colSize.x * shrink * 0.5;
      const halfD = colSize.z * shrink * 0.5;
      const wall = {
        minX: colCenter.x - halfW,
        maxX: colCenter.x + halfW,
        minZ: colCenter.z - halfD,
        maxZ: colCenter.z + halfD,
      };
      this.walls.push(wall);
      this._wallBoxes.push(wall);
      this._propColliders.push({
        x: colCenter.x,
        z: colCenter.z,
        width: halfW * 2,
        depth: halfD * 2,
        minX: wall.minX,
        maxX: wall.maxX,
        minZ: wall.minZ,
        maxZ: wall.maxZ,
      });
      this._navRevision++;
    });
  }

  _addFloorPattern(halfW, halfH, { dense = true } = {}) {
    const tileRng = (() => {
      let s = (this.seed ^ 0xdeadbeef) | 0;
      return () => {
        s = (s * 1664525 + 1013904223) & 0xffffffff;
        return (s >>> 0) / 0xffffffff;
      };
    })();

    const floorMatA = new THREE.MeshStandardMaterial({
      color: FLOOR_COLOR,
      roughness: 0.92,
      metalness: 0,
    });
    const floorMatB = new THREE.MeshStandardMaterial({
      color: FLOOR_COLOR_ALT,
      roughness: 0.95,
      metalness: 0,
    });

    // Safety: only retint/reposition maze floor tiles.
    // Boss arena uses a single PlaneGeometry floor and should stay flat.
    // We additionally gate by tile-like dimensions to avoid future prop collisions.
    this.group.traverse((node) => {
      if (!node.isMesh || !node.geometry) return;
      if (node.geometry.type !== 'BoxGeometry') return;
      const params = node.geometry.parameters;
      if (!params) return;
      if (Math.abs(params.width - CELL_SIZE) > 0.001) return;
      if (Math.abs(params.depth - CELL_SIZE) > 0.001) return;
      if (Math.abs(params.height - 0.14) > 0.001) return;
      const y = node.position.y;
      if (y <= -0.2 || y >= 0.1) return;
      const offset = (tileRng() - 0.5) * 2 * TILE_H_VARIANCE;
      node.position.y = offset - 0.07;
      const cx = Math.round(node.position.x / CELL_SIZE);
      const cy = Math.round(node.position.z / CELL_SIZE);
      node.material = (cx + cy) % 2 === 0 ? floorMatA : floorMatB;
    });

    const size = Math.max(halfW * 2, halfH * 2);
    const div = dense ? Math.max(26, Math.floor(size / 1.6)) : Math.max(18, Math.floor(size / 2.2));
    const grid = new THREE.GridHelper(size, div, 0x1a2234, 0x151d2b);
    grid.position.y = 0.04;
    grid.material.opacity = dense ? 0.18 : 0.12;
    grid.material.transparent = true;
    this.group.add(grid);
    this._gridGlowOverlay = createGridGlowOverlay(size, div, 0.052);
    this.group.add(this._gridGlowOverlay);

    const markMat = new THREE.MeshBasicMaterial({
      color: FLOOR_MARK_COLOR,
      transparent: true,
      opacity: dense ? 0.09 : 0.06,
      depthWrite: false,
    });
    const strips = [
      { w: halfW * 1.1, d: 0.08, x: 0, z: 0 },
      { w: 0.08, d: halfH * 1.1, x: 0, z: 0 },
      { w: halfW * 0.4, d: 0.06, x: -halfW * 0.42, z: -halfH * 0.42 },
      { w: halfW * 0.4, d: 0.06, x: halfW * 0.42, z: halfH * 0.42 },
      { w: 0.06, d: halfH * 0.4, x: halfW * 0.42, z: -halfH * 0.42 },
      { w: 0.06, d: halfH * 0.4, x: -halfW * 0.42, z: halfH * 0.42 },
    ];
    for (const s of strips) {
      const mark = new THREE.Mesh(new THREE.PlaneGeometry(s.w, s.d), markMat);
      mark.rotation.x = -Math.PI / 2;
      mark.position.set(s.x, 0.04, s.z);
      this.group.add(mark);
    }
    this._buildPlayerLightLayer(halfW, halfH);
  }

  _buildPlayerLightLayer(halfW, halfH) {
    const floorGlow = LIGHTING_QUALITY.cinematic.floorGlow;
    this._playerLightGlowConfig = {
      baseOpacity: floorGlow.baseOpacity,
      maxOpacity: floorGlow.maxOpacity,
      radiusMul: floorGlow.radiusMul,
    };
    const inner = new THREE.Mesh(
      new THREE.CircleGeometry(2.8, 32),
      new THREE.MeshBasicMaterial({
        color: 0xff4d88,
        transparent: true,
        opacity: floorGlow.baseOpacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    inner.rotation.x = -Math.PI / 2;
    inner.position.y = 0.06;
    inner.renderOrder = 3;
    this.group.add(inner);
    this._playerGlowInner = inner;

    const outer = new THREE.Mesh(
      new THREE.CircleGeometry(5.4, 40),
      new THREE.MeshBasicMaterial({
        color: 0xff2b68,
        transparent: true,
        opacity: floorGlow.baseOpacity * 0.58,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    outer.rotation.x = -Math.PI / 2;
    outer.position.y = 0.055;
    outer.renderOrder = 2;
    this.group.add(outer);
    this._playerGlowOuter = outer;

    const veil = new THREE.Mesh(
      new THREE.PlaneGeometry(halfW * 2, halfH * 2),
      new THREE.MeshBasicMaterial({
        color: 0xff265b,
        transparent: true,
        opacity: 0.022,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    veil.rotation.x = -Math.PI / 2;
    veil.position.y = 0.05;
    veil.renderOrder = 1;
    this.group.add(veil);
  }

  updatePlayerLightGlow(player, dt = 0) {
    if (!this._playerGlowInner || !player) return;
    const baseRadius = 6.8 * this._playerLightGlowConfig.radiusMul;
    const now = performance.now() * 0.001;
    const innerPulse = 1 + Math.sin(now * 4.2 + dt * 7) * 0.06;
    const outerPulse = 1 + Math.sin(now * 2.1 + 0.6) * 0.05;
    this._playerGlowInner.position.set(player.x, 0.06, player.z);
    this._playerGlowOuter.position.set(player.x, 0.055, player.z);
    this._playerGlowInner.scale.setScalar((baseRadius * 0.55 * innerPulse) / 2.8);
    this._playerGlowOuter.scale.setScalar((baseRadius * outerPulse) / 5.4);
    const fade = THREE.MathUtils.clamp((Math.sin(now * 2.7) + 1) * 0.5, 0, 1);
    this._playerGlowInner.material.opacity = THREE.MathUtils.lerp(
      this._playerLightGlowConfig.baseOpacity,
      this._playerLightGlowConfig.maxOpacity,
      fade,
    );
    this._playerGlowOuter.material.opacity = this._playerGlowInner.material.opacity * 0.56;
    updateGridGlowOverlay(this._gridGlowOverlay, {
      playerX: player.x,
      playerZ: player.z,
      playerRadius: baseRadius * 1.05,
      lampPoints: this._gridLampPoints || [],
      lampRadius: baseRadius,
    });
  }

  getAtmosphereBounds() {
    return {
      minX: this._boundsHalfW * -1,
      maxX: this._boundsHalfW,
      minZ: this._boundsHalfH * -1,
      maxZ: this._boundsHalfH,
    };
  }

  getBounds() {
    return this.getAtmosphereBounds();
  }

  getDoorPosition() {
    return this.door ? { x: this.door.x, z: this.door.z } : null;
  }

  getProps() {
    return this._propColliders.map((p) => ({ ...p }));
  }

  _addPerimeterAccentLights(halfW, halfH, { dense = true } = {}) {
    const insetX = Math.max(3.2, halfW - 0.3);
    const insetZ = Math.max(3.2, halfH - 0.3);
    const bracketMat = new THREE.MeshStandardMaterial({
      color: 0x1c2230,
      roughness: 0.95,
      metalness: 0.02,
    });

    // Pull practicals inward and widen reach so maze lanes are actually lit.
    const practicals = [
      { x: -insetX * 0.72, y: 2.5, z: -insetZ * 0.24, color: 0xff9944, intensity: 3.2, distance: 18, decay: 2.0 },
      { x: insetX * 0.72, y: 2.5, z: -insetZ * 0.24, color: 0xff8833, intensity: 2.9, distance: 18, decay: 2.0 },
      { x: insetX * 0.55, y: 2.2, z: -insetZ * 0.58, color: 0x44aaff, intensity: 1.8, distance: 13, decay: 2.0 },
      { x: 0, y: 3.1, z: insetZ * 0.08, color: 0xff7722, intensity: 1.8, distance: 20, decay: 2.0 },
    ];
    this._gridLampPoints = practicals.map((p) => ({ x: p.x, z: p.z }));
    const count = dense ? practicals.length : 3;
    const shadowCap = Math.min(count, LIGHTING_QUALITY.proceduralAccent.maxShadowedPointLights);
    for (let i = 0; i < count; i++) {
      const p = practicals[i];
      const color = p.color;
      const light = new THREE.PointLight(color, p.intensity, p.distance, p.decay);
      light.position.set(p.x, p.y - 0.2, p.z);
      light.castShadow = i < shadowCap;
      if (light.castShadow) {
        const quality = LIGHTING_QUALITY.proceduralAccent;
        light.shadow.mapSize.set(quality.pointShadowMapSize, quality.pointShadowMapSize);
        light.shadow.camera.near = quality.pointShadowCameraNear;
        light.shadow.camera.far = quality.pointShadowCameraFar;
        light.shadow.bias = quality.pointShadowBias;
        light.shadow.normalBias = quality.pointShadowNormalBias;
      }
      this.group.add(light);
      // Non-shadow bounce adds perceived glow/ambient around practical lights.
      const bounce = new THREE.PointLight(color, p.intensity * 0.28, p.distance * 1.45, 1.35);
      bounce.position.set(p.x, Math.max(0.7, p.y - 1.0), p.z);
      this.group.add(bounce);

      const bracket = new THREE.Mesh(
        new THREE.BoxGeometry(0.12, 0.12, 0.12),
        bracketMat,
      );
      bracket.position.set(p.x, p.y, p.z);
      bracket.castShadow = true;
      this.group.add(bracket);

      const flame = new THREE.Mesh(
        new THREE.SphereGeometry(0.18, 8, 8),
        new THREE.MeshStandardMaterial({
          color,
          emissive: color,
          emissiveIntensity: dense ? 2.3 : 1.9,
          roughness: 0.3,
          metalness: 0,
        }),
      );
      flame.position.set(p.x, p.y - 0.15, p.z);
      this.group.add(flame);
      // Player-like glow bubble: broad radius + gentle falloff (same feel as player light).
      const glowBubble = new THREE.PointLight(color, dense ? 140 : 110, dense ? 12 : 20, 1.4);
      glowBubble.position.set(p.x, p.y - 0.15, p.z);
      glowBubble.castShadow = false;
      this.group.add(glowBubble);

      const halo = new THREE.Mesh(
        new THREE.CircleGeometry(dense ? 2.4 : 1.8, 20),
        new THREE.MeshBasicMaterial({
          color,
          transparent: true,
          opacity: dense ? 0.08 : 0.06,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
        }),
      );
      halo.rotation.x = -Math.PI / 2;
      halo.position.set(p.x, 0.04, p.z);
      this.group.add(halo);
    }

    if (dense) {
      const centerFill = new THREE.PointLight(0xff6611, 2.8, halfW * 2.2, 1.5);
      centerFill.position.set(0, 3.5, 0);
      this.group.add(centerFill);
    }
  }

  _addPerimeterProps(halfW, halfH, { dense = true } = {}) {
    const positions = [
      { x: -halfW + 3.6, z: -halfH + 4.3 },
      { x: halfW - 3.6, z: -halfH + 4.3 },
      { x: -halfW + 3.6, z: halfH - 4.3 },
      { x: halfW - 3.6, z: halfH - 4.3 },
      { x: -halfW + 2.8, z: 0 },
      { x: halfW - 2.8, z: 0 },
      { x: 0, z: -halfH + 3.1 },
      { x: 0, z: halfH - 3.1 },
    ];
    const count = dense ? positions.length : 4;
    const pillarMat = new THREE.MeshStandardMaterial({
      color: 0x252f3e,
      roughness: 0.94,
      metalness: 0,
    });
    const capMat = new THREE.MeshStandardMaterial({
      color: 0x3a4a5c,
      roughness: 0.88,
      metalness: 0,
    });
    const coffinMat = new THREE.MeshStandardMaterial({
      color: 0x2e3d52,
      roughness: 0.9,
      metalness: 0.04,
    });
    for (let i = 0; i < count; i++) {
      const p = this._decorPointNear(positions[i], { maxDist: dense ? 14 : 6 }) || positions[i];
      const nearDoor = this.doorA && Math.hypot(p.x - this.doorA.x, p.z - this.doorA.z) < 4.8;
      if (nearDoor) continue;
      const kind = i % 3;

      if (kind === 0) {
        const w = 1.1;
        const h = 2.6;
        const d = 1.1;
        const pillar = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), pillarMat);
        pillar.position.set(p.x, h * 0.5, p.z);
        pillar.castShadow = true;
        pillar.receiveShadow = true;
        this.group.add(pillar);

        const cap = new THREE.Mesh(new THREE.BoxGeometry(w * 1.18, 0.2, d * 1.18), capMat);
        cap.position.set(p.x, h + 0.1, p.z);
        this.group.add(cap);

        const base = new THREE.Mesh(new THREE.BoxGeometry(w * 1.22, 0.18, d * 1.22), capMat);
        base.position.set(p.x, 0.09, p.z);
        this.group.add(base);
      } else if (kind === 1) {
        const w = 1.8;
        const h = 0.55;
        const d = 1.05;
        const slab = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), coffinMat);
        slab.position.set(p.x, h * 0.5, p.z);
        slab.rotation.y = i % 2 === 0 ? 0 : Math.PI * 0.5;
        slab.castShadow = true;
        slab.receiveShadow = true;
        this.group.add(slab);

        const lid = new THREE.Mesh(new THREE.BoxGeometry(w * 0.9, 0.06, d * 0.9), capMat);
        lid.position.set(p.x, h + 0.03, p.z);
        lid.rotation.y = slab.rotation.y;
        this.group.add(lid);
      } else {
        const w = 1.35;
        const h = 1.5;
        const d = 1.6;
        const altar = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), pillarMat);
        altar.position.set(p.x, h * 0.5, p.z);
        altar.castShadow = true;
        altar.receiveShadow = true;
        this.group.add(altar);

        const cap = new THREE.Mesh(new THREE.BoxGeometry(w * 0.95, 0.1, d * 0.95), capMat);
        cap.position.set(p.x, h + 0.05, p.z);
        this.group.add(cap);
      }

      const propW = kind === 0 ? 1.1 : (kind === 1 ? 1.8 : 1.35);
      const propD = kind === 0 ? 1.1 : (kind === 1 ? 1.05 : 1.6);
      const rotated = kind === 1 && i % 2 !== 0;
      const wall = {
        minX: p.x - (rotated ? propD : propW) * 0.5,
        maxX: p.x + (rotated ? propD : propW) * 0.5,
        minZ: p.z - (rotated ? propW : propD) * 0.5,
        maxZ: p.z + (rotated ? propW : propD) * 0.5,
      };
      this.walls.push(wall);
      this._wallBoxes.push(wall);
      this._propColliders.push({
        x: (wall.minX + wall.maxX) * 0.5,
        z: (wall.minZ + wall.maxZ) * 0.5,
        width: wall.maxX - wall.minX,
        depth: wall.maxZ - wall.minZ,
        minX: wall.minX,
        maxX: wall.maxX,
        minZ: wall.minZ,
        maxZ: wall.maxZ,
      });
    }
  }

  _decorPointNear(target, { maxDist = 10 } = {}) {
    if (!this._walkableCells.length) return null;
    let best = null;
    let bestD = Infinity;
    for (const cell of this._walkableCells) {
      const d = Math.hypot(cell.x - target.x, cell.z - target.z);
      if (d > maxDist || d >= bestD) continue;
      if (!this._hasClearance(cell.x, cell.z, 1.4)) continue;
      if (this.doorA && Math.hypot(cell.x - this.doorA.x, cell.z - this.doorA.z) < 5.2) continue;
      if (this.doorB && Math.hypot(cell.x - this.doorB.x, cell.z - this.doorB.z) < 4.2) continue;
      best = cell;
      bestD = d;
    }
    return best ? { x: best.x, z: best.z } : null;
  }

  _isWalkableWorld(x, z) {
    const { cx, cy } = worldToCell(x, z);
    return inBounds(cx, cy) && this._walkable.has(keyOf(cx, cy));
  }

  isWalkableSpawn(x, z, radius = 0) {
    if (!Number.isFinite(x) || !Number.isFinite(z)) return false;
    const r = Math.max(0, radius || 0);
    if (r <= 0.01) return this._isWalkableWorld(x, z);
    if (!this._isWalkableWorld(x, z)) return false;
    const dirs = [
      [1, 0], [-1, 0], [0, 1], [0, -1],
      [0.707, 0.707], [-0.707, 0.707], [0.707, -0.707], [-0.707, -0.707],
    ];
    for (const [dx, dz] of dirs) {
      if (!this._isWalkableWorld(x + dx * r, z + dz * r)) return false;
    }
    return true;
  }

  _pickBestSpawnForZone(zone, opts = {}) {
    const clearRadius = opts.clearRadius ?? 1.2;
    const edgeSide = opts.edgeSide || 'top';
    const minDistFromPlayer = opts.minDistFromPlayer ?? 0;
    const filter = (cell) => {
      const { x, z } = cell;
      if (minDistFromPlayer > 0) {
        const d = Math.hypot(x - this.playerSpawn.x, z - this.playerSpawn.z);
        if (d < minDistFromPlayer) return false;
      }
      if (zone === 'near') return z >= 2 * CELL_SIZE && z <= this._boundsHalfH - 2 * CELL_SIZE;
      if (zone === 'far') return z >= -this._boundsHalfH + 2 * CELL_SIZE && z <= -2 * CELL_SIZE;
      if (zone === 'center') return Math.abs(x) <= 5 * CELL_SIZE && Math.abs(z) <= 5 * CELL_SIZE;
      if (zone === 'diagonal') {
        if (!(z <= -this._boundsHalfH + 8 * CELL_SIZE && Math.abs(Math.abs(x) - (this._boundsHalfW - 8 * CELL_SIZE)) <= 4 * CELL_SIZE)) return false;
        if (edgeSide === 'left') return x < 0;
        if (edgeSide === 'right') return x > 0;
        return true;
      }
      if (zone === 'edge') {
        if (edgeSide === 'left') return x <= -this._boundsHalfW + 3 * CELL_SIZE;
        if (edgeSide === 'right') return x >= this._boundsHalfW - 3 * CELL_SIZE;
        if (edgeSide === 'bottom') return z >= this._boundsHalfH - 3 * CELL_SIZE;
        return z <= -this._boundsHalfH + 3 * CELL_SIZE;
      }
      return true;
    };
    const candidates = this._walkableCells.filter(filter);
    if (candidates.length === 0) return null;

    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[(i * 11) % candidates.length];
      if (this.isWalkableSpawn(c.x, c.z, clearRadius) && this._hasClearance(c.x, c.z, clearRadius)) {
        return { x: c.x, z: c.z };
      }
    }
    for (const c of candidates) {
      if (this.isWalkableSpawn(c.x, c.z, 0.2)) return { x: c.x, z: c.z };
    }
    return null;
  }

  _hasClearance(x, z, radius) {
    for (const w of this._wallBoxes) {
      const nx = Math.max(w.minX, Math.min(x, w.maxX));
      const nz = Math.max(w.minZ, Math.min(z, w.maxZ));
      const dx = x - nx;
      const dz = z - nz;
      if ((dx * dx + dz * dz) <= radius * radius) return false;
    }
    return true;
  }

  _buildEnemyAnchors() {
    const anchors = [];
    const zones = ['center', 'near', 'far', 'diagonal', 'edge', 'near'];
    for (const zone of zones) {
      const edgeSide = zone === 'edge' ? 'top' : undefined;
      const p = this._pickBestSpawnForZone(zone, {
        edgeSide,
        clearRadius: 1.4,
        minDistFromPlayer: MIN_ENEMY_PLAYER_DIST,
      });
      if (!p) continue;
      if (anchors.some((a) => Math.hypot(a.x - p.x, a.z - p.z) < 7)) continue;
      anchors.push(p);
    }
    if (anchors.length === 0 && this._walkableCells.length) {
      const c = this._walkableCells[Math.floor(this._walkableCells.length / 2)];
      anchors.push({ x: c.x, z: c.z });
    }
    return anchors;
  }

  _edgeWalkableCandidates(edgeSide) {
    return this._walkableCells.filter(({ x, z }) => {
      if (edgeSide === 'bottom') return z >= this._boundsHalfH - 3 * CELL_SIZE;
      if (edgeSide === 'top') return z <= -this._boundsHalfH + 3 * CELL_SIZE;
      if (edgeSide === 'left') return x <= -this._boundsHalfW + 3 * CELL_SIZE;
      if (edgeSide === 'right') return x >= this._boundsHalfW - 3 * CELL_SIZE;
      return false;
    });
  }

  _fallbackDoorAnchor(edgeSide) {
    if (!this._walkableCells.length) return { x: 0, z: 0 };
    const axisScore = (p) => {
      if (edgeSide === 'bottom') return p.z;
      if (edgeSide === 'top') return -p.z;
      if (edgeSide === 'left') return -p.x;
      if (edgeSide === 'right') return p.x;
      return 0;
    };
    let best = null;
    let bestScore = -Infinity;
    for (const p of this._walkableCells) {
      if (!this._hasClearance(p.x, p.z, 0.8)) continue;
      const score = axisScore(p) - Math.abs(p.x) * 0.04;
      if (score > bestScore) {
        bestScore = score;
        best = p;
      }
    }
    if (!best) {
      const mid = this._walkableCells[Math.floor(this._walkableCells.length / 2)];
      return { x: mid.x, z: mid.z };
    }
    return { x: best.x, z: best.z };
  }

  _resolveDoorAnchors() {
    const bottom = this._edgeWalkableCandidates('bottom');
    const top = this._edgeWalkableCandidates('top');
    const fallbackA = this._pickBestSpawnForZone('edge', { edgeSide: 'bottom', clearRadius: 0.8 })
      || this._fallbackDoorAnchor('bottom');
    const fallbackB = this._pickBestSpawnForZone('edge', { edgeSide: 'top', clearRadius: 0.8 })
      || this._fallbackDoorAnchor('top');
    if (!bottom.length || !top.length) {
      this.doorA = fallbackA;
      this.doorB = fallbackB;
      return;
    }

    const targetX = (this._rng() * 2 - 1) * (this._boundsHalfW * 0.45);
    let doorA = bottom[0];
    let bestAX = Infinity;
    for (const c of bottom) {
      if (!this._hasClearance(c.x, c.z, 0.8)) continue;
      const dx = Math.abs(c.x - targetX);
      if (dx < bestAX) { bestAX = dx; doorA = c; }
    }

    let doorB = top[0];
    let bestScore = -Infinity;
    for (const c of top) {
      if (!this._hasClearance(c.x, c.z, 0.8)) continue;
      const dist = Math.hypot(c.x - doorA.x, c.z - doorA.z);
      const oppositeXBias = Math.abs((c.x + doorA.x) / Math.max(1, this._boundsHalfW));
      const score = dist - oppositeXBias * 2;
      if (score > bestScore) { bestScore = score; doorB = c; }
    }

    if (Math.hypot(doorB.x - doorA.x, doorB.z - doorA.z) < MIN_DOOR_SEPARATION) {
      doorB = fallbackB;
    }

    this.doorA = { x: doorA.x, z: doorA.z };
    this.doorB = { x: doorB.x, z: doorB.z };
  }

  _buildExitDoor() {
    const p = this.doorB || this._fallbackDoorAnchor('top');
    const geo = new THREE.BoxGeometry(1.1 * CELL_SIZE, 2.2, 0.4 * CELL_SIZE);
    const mat = new THREE.MeshStandardMaterial({
      color: DOOR_COLOR_LOCKED,
      emissive: DOOR_COLOR_LOCKED,
      emissiveIntensity: 0.5,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(p.x, 1.1, p.z);
    mesh.castShadow = true;
    this.group.add(mesh);
    this.door = { x: p.x, z: p.z, mesh, material: mat, blockerWall: null };
    this.door.blockerWall = {
      minX: p.x - this.doorBlockerHalfWidth,
      maxX: p.x + this.doorBlockerHalfWidth,
      minZ: p.z - this.doorBlockerHalfDepth,
      maxZ: p.z + this.doorBlockerHalfDepth,
    };
    this.walls.push(this.door.blockerWall);
  }

  resolveEncounterSpawn(strategy, options = {}) {
    const minClearRadius = Math.max(1.2, options.minClearRadius || 1.2);
    if (strategy === 'diagonal') {
      this._diagToggle = !this._diagToggle;
      const edgeSide = this._diagToggle ? 'right' : 'left';
      const pos = this._pickBestSpawnForZone('diagonal', {
        edgeSide,
        clearRadius: minClearRadius,
        minDistFromPlayer: MIN_ENEMY_PLAYER_DIST,
      });
      if (pos) return pos;
    }
    if (strategy === 'edge') {
      const pos = this._pickBestSpawnForZone('edge', {
        edgeSide: options.edgeSide || 'top',
        clearRadius: minClearRadius,
        minDistFromPlayer: MIN_ENEMY_PLAYER_DIST,
      });
      if (pos) return pos;
    }
    if (strategy === 'near') {
      const pos = this._pickBestSpawnForZone('near', {
        clearRadius: minClearRadius,
        minDistFromPlayer: MIN_ENEMY_PLAYER_DIST,
      }) || this._pickBestSpawnForZone('center', {
        clearRadius: Math.max(2, minClearRadius),
        minDistFromPlayer: Math.max(8, MIN_ENEMY_PLAYER_DIST - 2),
      });
      if (pos) return pos;
    }
    if (strategy === 'center' || strategy === 'far') {
      const pos = this._pickBestSpawnForZone(strategy, {
        clearRadius: strategy === 'center' ? Math.max(2.6, minClearRadius) : minClearRadius,
        minDistFromPlayer: MIN_ENEMY_PLAYER_DIST,
      });
      if (pos) return pos;
    }
    return this._pickBestSpawnForZone('far', {
      clearRadius: minClearRadius,
      minDistFromPlayer: MIN_ENEMY_PLAYER_DIST,
    }) || this._pickBestSpawnForZone('edge', { edgeSide: 'top', clearRadius: minClearRadius }) || this.playerSpawn;
  }

  unlockDoor() {
    if (!this.door || this.doorOpen) return;
    this.doorOpen = true;
    this.door.material.color.setHex(DOOR_COLOR_OPEN);
    this.door.material.emissive.setHex(DOOR_COLOR_OPEN);
    this.door.mesh.position.y = 3.5;
    const idx = this.walls.indexOf(this.door.blockerWall);
    if (idx !== -1) this.walls.splice(idx, 1);
    this._navRevision++;
  }
  breachAt(x, z, radius = 1.4) {
    let removed = 0;
    const r2 = radius * radius;
    for (let i = this.walls.length - 1; i >= 0; i--) {
      const w = this.walls[i];
      if (w === this.door?.blockerWall) continue;
      const cx = (w.minX + w.maxX) * 0.5;
      const cz = (w.minZ + w.maxZ) * 0.5;
      const dx = cx - x;
      const dz = cz - z;
      if (dx * dx + dz * dz <= r2) {
        this.walls.splice(i, 1);
        const idxWall = this._wallBoxes.indexOf(w);
        if (idxWall !== -1) this._wallBoxes.splice(idxWall, 1);
        removed++;
      }
    }
    if (removed > 0) this._navRevision++;
    return removed > 0;
  }

  playerAtDoor(px, pz) {
    if (!this.door || !this.doorOpen) return false;
    return Math.hypot(px - this.door.x, pz - this.door.z) <= 1.6 * CELL_SIZE;
  }

  dispose() {
    this._isDisposed = true;
    this.scene.remove(this.group);
    this.group.traverse((c) => {
      c.geometry?.dispose?.();
    });
  }
}

